import { ArrowLeft, Pencil, Trash } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";

interface ProductDetailsProps {
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getCategoryTitle: (categoryId: string) => string;
  product: {
    quantity: string;
    status: string;
    category_id: string;
    media_url: any;
    additional_features: string;
    product_image_path: string;
    title: string;
    price: any;
    name: string;
    id: string;
    category: string;
    expiryDate: string;
    buyingPrice: number;
    description: string;
    keyFeatures: string[];
    image?: string;
    openingStock: number;
    remainingStock: number;
    purchased: number;
  };
}

export function ProductDetails({
  onBack,
  onEdit,
  onDelete,
  getCategoryTitle,
  product,
}: ProductDetailsProps) {
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-[#B87775]">
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-medium">BACK TO INVENTORY</span>
      </div>

      <Card className="p-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-semibold">{product.title}</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="gap-2 text-[#B87775] border border-[#B87775] bg-white"
              onClick={onEdit}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            <Button
              variant="destructive"
              className="gap-2 bg-red-500 border text-white"
              onClick={onDelete}
            >
              <Trash className="h-4 w-4" />
              Delete
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="border-b h-auto p-0 mb-6">
            <TabsTrigger
              value="overview"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B87775] data-[state=active]:text-[#B87775]"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="purchases"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#B87775] data-[state=active]:text-[#B87775]"
            >
              Purchases
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-0">
            <div className="grid md:grid-cols-[1fr,300px] gap-8">
              <div className="space-y-8">
                <div>
                  <h2 className="text-lg font-semibold mb-4">
                    Primary Details
                  </h2>
                  <dl className="grid gap-4">
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-muted-foreground">Product Name:</dt>
                      <dd className="col-span-2 font-bold text-lg">{product.title}</dd>
                    </div>
                    {/* <div className="grid grid-cols-3 gap-4">
                      <dt className="text-muted-foreground">Product ID</dt>
                      <dd className="col-span-2 font-bold text-lg">{product.id}</dd>
                    </div> */}
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-muted-foreground">Category:</dt>
                      <dd className="col-span-2 font-bold text-lg">{getCategoryTitle(product.category_id)}</dd>
                    </div>
                    {/* <div className="grid grid-cols-3 gap-4">
                      <dt className="text-muted-foreground">Expiry Date</dt>
                      <dd className="col-span-2 font-bold text-lg">{product.expiryDate}</dd>
                    </div> */}
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-muted-foreground">Price:</dt>
                      <dd className="col-span-2 font-bold text-lg">
                        ${product.price.toFixed(2)}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h2 className="text-lg font-semibold mb-4">Description</h2>
                  <p className="text-muted-foreground">{product.description}</p>
                </div>

                <div>
                  <h2 className="text-lg font-semibold mb-4">Key Features</h2>
                  <ul className="pl-4 space-y-2">
                    {product.additional_features  && product.additional_features
                      .split(/\r\n/)
                      .map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                  </ul>
                </div>
              </div>

              <div>
                <div className="border-2 border-dashed rounded-lg p-4 mb-6">
                  {product.media_url ? (
                    <img
                      src={product.media_url || "/placeholder.svg"}
                      alt={product.name}
                      className="w-full h-auto rounded-lg"
                    />
                  ) : (
                    <div className="aspect-square rounded-lg bg-muted flex items-center justify-center">
                      No Image
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Product Quantity</span>
                    <span className="font-medium">{product.quantity}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">
                      Product Status
                    </span>
                    <span className="font-medium capitalize">
                      {product.status}
                    </span>
                  </div>
                  {/* <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Purchased</span>
                    <span className="font-medium">{product.purchased}</span>
                  </div> */}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="purchases">
            <p>Purchase history will be displayed here</p>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
